// Scroll reveal — every section in the content fades and rises as it enters the
// viewport, with its repeated children (cards, rows, columns) staggered after it.

// Sections that must never be hidden or transformed.
const SKIP_SECTIONS = [
  '.section-marquee',
  '.section-testimonials',
  '.klik-subscribe-popup',
  '.kd-cookie-bar',
  '.wp-block-spacer',
  '.wp-block-separator',
];

// Continuous/scrubbed sections: fade only, never transform, so their own
// GSAP transforms and measurements stay untouched.
const FADE_ONLY = ['.parallax-section', '.section-portfolio-results', '.kd-marquee'];

// Never stagger inside these — sliders and accordion panels own their children.
const NO_STAGGER_INSIDE = ['.swiper', '.klik-faq-item__answer', '.parallax-row', '.section-marquee__row'];

const MAX_STAGGER_ITEMS = 12;
const STEP = 0.08;
const BASE_DELAY = 0.15;

function matchesAny(el, selectors) {
  return selectors.some((s) => el.matches(s));
}

function closestAny(el, selectors, root) {
  let n = el;
  while (n && n !== root) {
    if (matchesAny(n, selectors)) return n;
    n = n.parentElement;
  }
  return null;
}

// The outermost container in a section whose element children all share one
// class — the repeated cards/rows/columns worth staggering.
function findStaggerGroup(section) {
  const nodes = [ section, ...section.querySelectorAll('*') ];

  for (const el of nodes) {
    if (closestAny(el, NO_STAGGER_INSIDE, section)) continue;

    const kids = Array.from(el.children).filter((k) => k.nodeType === 1);
    if (kids.length < 2 || kids.length > MAX_STAGGER_ITEMS) continue;

    const sig = ( kids[0].getAttribute('class') || '' ).trim();
    if (!sig) continue;
    if (!kids.every((k) => ( k.getAttribute('class') || '' ).trim() === sig)) continue;

    // Skip inline runs (tags, buttons) — only stack/grid-sized items.
    if (kids.some((k) => k.getBoundingClientRect().height < 40)) continue;

    return kids;
  }

  return [];
}

// A section that paints its own solid dark background must not fade or move as
// a whole — the lighter page behind it flashes through as a seam.
function hasSolidDarkBg(el) {
  const m = getComputedStyle(el).backgroundColor.match(/rgba?\(([^)]+)\)/);
  if (!m) return false;

  const p = m[1].split(',').map((n) => parseFloat(n));
  if (p.length > 3 && p[3] < 0.9) return false;

  return 0.299 * p[0] + 0.587 * p[1] + 0.114 * p[2] < 90;
}

// Real layout children: sections wrap content in an inner container that is
// display:contents, so the boxes worth animating can sit one level deeper.
function contentChildren(section) {
  const inner = section.querySelector(':scope > .wp-block-group__inner-container');
  const host = inner || section;

  return Array.from(host.children).filter(
    (k) => k.nodeType === 1 && k.getBoundingClientRect().height > 0
  );
}

function prepare(el, index, fadeOnly) {
  el.setAttribute('data-reveal', fadeOnly ? 'fade' : '');
  if (index > 0) {
    el.style.setProperty('--kd-reveal-delay', ( BASE_DELAY + ( index - 1 ) * STEP ).toFixed(2) + 's');
  }
}

export default function initReveal() {
  const root = document.documentElement;

  if (document.body.classList.contains('web-studio-page')) {
    root.classList.remove('kd-anim');
    return;
  }

  const container = document.querySelector('.entry-content') || document.querySelector('.site-main');
  if (!container) {
    root.classList.remove('kd-anim');
    return;
  }

  const sections = Array.from(container.children).filter(
    (el) => el.nodeType === 1 && !matchesAny(el, SKIP_SECTIONS) && el.getBoundingClientRect().height > 0
  );

  let pending = [];

  sections.forEach((section) => {
    const fadeOnly = matchesAny(section, FADE_ONLY);

    if (hasSolidDarkBg(section)) {
      // Background stays put; only the content inside reveals.
      contentChildren(section).forEach((child, i) => {
        prepare(child, i, fadeOnly);
        pending.push(child);
      });
    } else {
      prepare(section, 0, fadeOnly);
      pending.push(section);
    }

    findStaggerGroup(section).forEach((child, i) => {
      if (child.hasAttribute('data-reveal')) return;
      prepare(child, i + 1, fadeOnly);
      child.setAttribute('data-reveal-child', '');
      pending.push(child);
    });
  });

  let ticking = false;

  // Position sweep rather than IntersectionObserver: a fast scroll or an
  // anchor jump can skip IO frames and strand an element at opacity 0.
  function check() {
    ticking = false;
    const line = window.innerHeight * 0.92;

    for (let i = pending.length - 1; i >= 0; i -= 1) {
      if (pending[i].getBoundingClientRect().top < line) {
        pending[i].classList.add('is-revealed');
        pending.splice(i, 1);
      }
    }

    if (!pending.length) stop();
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(check);
  }

  function stop() {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
    document.removeEventListener('visibilitychange', check);
    window.removeEventListener('load', check);
    if (window.__kdLenis) window.__kdLenis.off('scroll', onScroll);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('visibilitychange', check);
  if (window.__kdLenis) window.__kdLenis.on('scroll', onScroll);

  // The hero is above the fold — show it on load rather than on scroll.
  const hero = sections[0];
  if (hero && hero.matches('.hero, .section-ebooks-hero, .section-thankyou-hero')) {
    pending = pending.filter((el) => el !== hero && !hero.contains(el));
    const showHero = () => {
      hero.classList.add('is-revealed');
      hero.querySelectorAll('[data-reveal]').forEach((c) => c.classList.add('is-revealed'));
    };
    // rAF for a real transition; the timeout covers a tab opened in the background.
    requestAnimationFrame(showHero);
    setTimeout(showHero, 120);
  }

  check();
  // Images and webfonts shift the layout — re-check once everything has settled.
  window.addEventListener('load', check);

  window.__kdRevealReady = true;
}
